import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CheckCircle2, Star } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { brand } from "../mock";

export default function TestimonialForm() {
  const [form, setForm] = useState({ name: "", company: "", role: "", text: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      setError("Please fill in your name and testimonial.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/testimonials/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submit failed");
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#faf7f7] via-white to-[#f5eeee] flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-[#E5E5E5] h-16 flex items-center px-6">
        <a href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-xl bg-white grid place-items-center overflow-hidden border-2 border-[#73100D]/30 shadow-sm">
            <img src={brand.logo} alt="RSP Innovations" className="w-9 h-9 object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-tight text-[#111]">
              RSP <span className="text-[#73100D]">Innovations</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#888]">RSPInnov LLP</div>
          </div>
        </a>
      </header>

      {/* Body */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-3xl border border-[#E5E5E5] p-10 text-center shadow-sm"
              >
                <div className="w-16 h-16 rounded-full bg-green-50 border-2 border-green-200 grid place-items-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-[#111]">Thank you, {form.name}!</h2>
                <p className="mt-3 text-[#555] leading-relaxed">
                  Your testimonial has been submitted and will appear on our website after review. We truly appreciate your kind words!
                </p>
                <a href="/" className="mt-8 inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[#73100D] text-white font-semibold text-sm hover:bg-[#5E0000] transition-colors">
                  Visit RSP Innovations →
                </a>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Heading */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-4">
                    Share Your Experience
                  </div>
                  <h1 className="text-3xl font-bold text-[#111] leading-tight">
                    How has RSP Innovations<br />
                    <span className="text-[#73100D]">helped your business?</span>
                  </h1>
                  <p className="mt-3 text-[#666] text-sm leading-relaxed max-w-sm mx-auto">
                    Your feedback helps us grow and helps others discover the right solution path. Takes less than 2 minutes.
                  </p>
                  <div className="mt-4 flex justify-center gap-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <Star key={s} className="w-5 h-5 fill-[#73100D] text-[#73100D]" />
                    ))}
                  </div>
                </div>

                {/* Form card */}
                <form onSubmit={onSubmit} className="bg-white rounded-3xl border border-[#E5E5E5] p-8 shadow-sm space-y-5">
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{error}</div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="block">
                      <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-1.5 font-semibold">Your Name <span className="text-[#73100D]">*</span></div>
                      <Input name="name" value={form.name} onChange={onChange} placeholder="Jane Doe"
                        className="h-11 border-[#E5E5E5] focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
                    </label>
                    <label className="block">
                      <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-1.5 font-semibold">Company Name</div>
                      <Input name="company" value={form.company} onChange={onChange} placeholder="Acme Corp"
                        className="h-11 border-[#E5E5E5] focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
                    </label>
                  </div>

                  <label className="block">
                    <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-1.5 font-semibold">Your Role / Designation</div>
                    <Input name="role" value={form.role} onChange={onChange} placeholder="CEO, Operations Manager, CTO…"
                      className="h-11 border-[#E5E5E5] focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
                  </label>

                  <label className="block">
                    <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-1.5 font-semibold">Your Testimonial <span className="text-[#73100D]">*</span></div>
                    <Textarea name="text" value={form.text} onChange={onChange} rows={5}
                      placeholder="Tell us about your experience working with RSP Innovations — what problem did we solve, what results did you see?"
                      className="border-[#E5E5E5] focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D] resize-none" />
                  </label>

                  <button type="submit" disabled={loading}
                    className="w-full h-12 rounded-full bg-[#73100D] text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#5E0000] transition-colors disabled:opacity-70 text-sm">
                    {loading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                      : <><Send className="w-4 h-4" /> Submit Testimonial</>}
                  </button>

                  <p className="text-center text-xs text-[#aaa]">
                    Submissions are reviewed before publishing. Your privacy is respected.
                  </p>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <footer className="text-center text-xs text-[#aaa] py-6">
        © {new Date().getFullYear()} RSP Innovations (RSPInnov LLP). All rights reserved.
      </footer>
    </div>
  );
}
